<template>
    <div class="currency-panel is-overlay">
        <header class="header">
            <button
                aria-label="Back to countries"
                class="back-nav"
                type="button"
                @click="emit('back')"
                @keydown.enter.prevent.stop="emit('back')"
            >
                <Icon
                    class="icon"
                    icon="angle-left"
                />

                <span class="title">Currency</span>
            </button>
        </header>

        <div class="scroll">
            <section
                aria-label="Currency"
                class="section"
                role="listbox"
            >
                <div class="list">
                    <CurrencyOption
                        v-for="currency in currencies"
                        :key="currency"
                        :currency="currency"
                        :selected="currencySelection === currency"
                        @select="emit('selectCurrency', $event)"
                    />
                </div>
            </section>
        </div>
    </div>
</template>

<script lang="ts" setup>
import CurrencyOption from '@/components/app/locale/CurrencyOption.vue'
import Icon from '@/components/ui/icon/Icon.vue'
import type { AppCurrency } from '@/plugins/currency-types'

const { currencies, currencySelection } = defineProps<{
    currencies: AppCurrency[]
    currencySelection: AppCurrency | undefined
}>()

const emit = defineEmits<{
    selectCurrency: [currency: AppCurrency]
    back: []
}>()
</script>

<style lang="scss" scoped>
.currency-panel {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    &.is-overlay {
        position: absolute;
        inset: 0;
        z-index: 2;
        background: $white;
    }

    .header {
        display: none;
        flex-shrink: 0;
        align-items: center;
        padding: 8px var(--spacing-16, 16px);
        border-bottom: 1px solid $grey-200;
        background: $white;

        @include min-width(640) {
            display: flex;
        }

        @include min-width(1024) {
            padding: 10px var(--spacing-20i, 20px);
        }
    }

    .back-nav {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        max-width: 100%;
        min-height: 28px;
        padding: 0;
        border: none;
        border-radius: var(--border-radius-m, 8px);
        background: transparent;
        font-family: inherit;
        text-align: left;
        color: $slate;
        cursor: pointer;
        @include default-transition();

        &:focus {
            outline: none;
        }

        &:focus-visible {
            outline: 2px solid $blue;
            outline-offset: 2px;
        }

        @include hover() {
            color: $blue;

            .title {
                color: $blue;
            }
        }

        .icon {
            display: inline-flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            font-size: 11px;
        }

        .title {
            font-family: $effra-font-stack;
            font-size: 0.875rem;
            font-weight: 400;
            line-height: 1.2;
            color: $slate;
            text-transform: none;
            letter-spacing: normal;
            white-space: nowrap;

            @include min-width(1024) {
                font-size: 0.9375rem;
            }
        }
    }

    .scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: var(--spacing-12, 12px) var(--spacing-16, 16px)
            var(--spacing-16, 16px);
        -webkit-overflow-scrolling: touch;

        @include min-width(1024) {
            padding: var(--spacing-12, 12px) var(--spacing-20i, 20px)
                var(--spacing-20i, 20px);
        }
    }

    .section {
        margin: 0;
    }

    .divider {
        border: none;
        border-top: 1px solid $grey-200;
        margin: 14px 0;
    }

    .list {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }
}
</style>
